import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { DispatchBoardQueueLoad, Door, DoorType, getDoorTypeDisplayLabel } from "app/data/city-delivery/dispatch-board/models";
import { useGetDoors, useSetupDoor } from "features/city-delivery/dispatch-board/hooks/useDoor";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Table from "ui-components/table/table";
import { getDoorColumns } from "./getDoorColumns";
import CarrierService from "app/data/carriers/carriersServices";
import customXGSSelectStyles from "ui-components/xgs-select/customXGSSelectStyles";
import Button, { ButtonThemes } from "ui-components/button/button";
import "ui-components/xgs-select/xgsSelect.scss";
import "./setupDoor.scss";
import { toast } from "react-toastify";

export interface CarrierOption {
    value: number;
    label: string;
}

export interface DoorTypeOption {
    value: DoorType;
    label: string;
}

const DOOR_TYPE_OPTIONS: DoorTypeOption[] = Object.values(DoorType).map((dt) => ({
    value: dt,
    label: getDoorTypeDisplayLabel(dt),
}));

const DEFAULT_DOOR_TYPES: DoorType[] = [DoorType.UNLOADING, DoorType.CITY_DELIVERY];

export interface SetupDoorProps {
    show: boolean;
    onClose: () => void;
    dispatchLoad: DispatchBoardQueueLoad;
}

const carrierService = CarrierService.getInstance();

const loadCarrierOptions = async (inputValue: string): Promise<CarrierOption[]> => {
    if (!inputValue || inputValue.trim().length < 2) {
        return [];
    }
    const response = await carrierService.getcarriersList(inputValue.trim(), 0);
    const content = response?.data?.content ?? [];
    return content.map((c) => ({
        value: c.carrierNumber,
        label: `${c.carrierName} (${c.carrierNumber})`,
    }));
};

const debounce = <T extends (...args: any[]) => any>(fn: T, ms: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        return new Promise<ReturnType<T>>((resolve) => {
            timeoutId = setTimeout(() => resolve(fn(...args)), ms);
        });
    };
};

function getInitialCarrierFromDispatchLoad(load: DispatchBoardQueueLoad): CarrierOption | null {
    if (load?.carrierNumber == null) return null;
    const label = load.carrierName
        ? `${load.carrierName} (${load.carrierNumber})`
        : String(load.carrierNumber);
    return { value: load.carrierNumber, label };
}

export const SetupDoor: React.FC<SetupDoorProps> = (props) => {
    const { show, onClose, dispatchLoad } = props;
    const [doorNumberSearch, setDoorNumberSearch] = useState("");
    const [doorTypes, setDoorTypes] = useState<DoorType[]>(DEFAULT_DOOR_TYPES);
    const [selectedDoor, setSelectedDoor] = useState<Door | null>(null);
    const [trailerNumber, setTrailerNumber] = useState("");
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierOption | null>(null);

    const userState: UserState = useSelector(userSelector);

    const {
        doorsData,
        isLoadingDoors,
    } = useGetDoors({
        terminalNumber: userState.activeTerminal?.id,
        doorNumberSearch: doorNumberSearch.trim() || undefined,
        doorTypes: doorTypes.length > 0 ? doorTypes : undefined,
    });

    const doors = useMemo(
        () => doorsData?.data?.doors ?? [],
        [doorsData?.data?.doors]
    );

    // Set initial values from props when sidebar opens
    useEffect(() => {
        if (show && dispatchLoad) {
            setSelectedDoor(null);
            setTrailerNumber(dispatchLoad.trailerNumber ?? "");
            setSelectedCarrier(getInitialCarrierFromDispatchLoad(dispatchLoad));
        }
    }, [show, dispatchLoad]);

    // When doors load, set selectedDoor from dispatchLoad.door only if not yet set (initial load)
    useEffect(() => {
        if (!show || !dispatchLoad?.door || doors.length === 0) return;
        setSelectedDoor((current) => {
            if (current) return current;
            const match = doors.find((d) => d.doorNumber === dispatchLoad?.door);
            return match ?? null;
        });
    }, [show, dispatchLoad?.door, doors]);

    const handleSetupSuccess = useCallback(() => {
        toast.info("Door setup successfully");
        onClose();
    }, [onClose]);

    const handleSetupError = useCallback((_e: any) => {
        const errorMessage =
            _e?.response?.data?.detail ?? _e?.data?.detail ?? "Failed to setup door";
        toast.error(errorMessage);
    }, []);

    const setupDoorMutation = useSetupDoor(handleSetupSuccess, handleSetupError);

    const loadOptions = useMemo(
        () => debounce(loadCarrierOptions, 300),
        []
    );

    const handleSubmit = useCallback(() => {
        const terminalNumber = userState.activeTerminal?.id;
        if (!selectedDoor?.doorNumber || !dispatchLoad?.picklistNumber || !terminalNumber) {
            return;
        }
        const trailerNum = trailerNumber.trim();
        setupDoorMutation.mutate({
            terminalNumber,
            doorNumber: selectedDoor.doorNumber,
            picklistNumber: dispatchLoad.picklistNumber,
            trailerNumber: trailerNum ? Number(trailerNum) : undefined,
            carrierNumber: selectedCarrier?.value,
        });
    }, [
        userState.activeTerminal?.id,
        selectedDoor?.doorNumber,
        dispatchLoad?.picklistNumber,
        trailerNumber,
        selectedCarrier,
        setupDoorMutation,
    ]);

    const isSubmitDisabled =
        !selectedDoor ||
        isLoadingDoors ||
        setupDoorMutation.isLoading;

    const doorTypeValues = useMemo(
        () => DOOR_TYPE_OPTIONS.filter((opt) => doorTypes.includes(opt.value)),
        [doorTypes]
    );

    return (
        <SlideOutSidebar
            header="Assign Door"
            show={show}
            onClose={onClose}
        >
            <div className="setup-door-form">
                <div className="setup-door-form__filters">
                    <div className="setup-door-form__filter setup-door-form__filter--search">
                        <label className="setup-door-form__label" htmlFor="setup-door-number-search">
                            Door number
                        </label>
                        <input
                            id="setup-door-number-search"
                            type="text"
                            className="setup-door-form__input"
                            value={doorNumberSearch}
                            onChange={(e) => setDoorNumberSearch(e.target.value)}
                            placeholder="Search door number"
                        />
                    </div>
                    <div className="setup-door-form__filter setup-door-form__filter--types">
                        <label className="setup-door-form__label">Door types</label>
                        <Select<DoorTypeOption>
                            isMulti
                            options={DOOR_TYPE_OPTIONS}
                            value={doorTypeValues}
                            onChange={(val) => {
                                const arr = Array.isArray(val) ? val : val ? [val] : [];
                                setDoorTypes(arr.length ? arr.map((o: DoorTypeOption) => o.value) : DEFAULT_DOOR_TYPES);
                            }}
                            placeholder="Select door types"
                            styles={customXGSSelectStyles}
                            classNamePrefix="xgs-select"
                            components={{ IndicatorSeparator: () => null }}
                        />
                    </div>
                </div>

                <div className="setup-door-form__table-section">
                    <label className="setup-door-form__label">Select door</label>
                    <div className="setup-door-form__table-wrap">
                        <Table
                            isLoading={isLoadingDoors}
                            columns={getDoorColumns(selectedDoor?.doorNumber)}
                            data={doors}
                            onRowClicked={(row: Door) => setSelectedDoor(row)}
                            cursorPointer={true}
                            noResultsText="No Door available"
                            onSelectedRowsChange={null}
                            infiniteScroll={true}
                            responsive={true}
                            minTableHeight={120}
                        />
                    </div>
                </div>

                <div className="setup-door-form__fields-row">
                    <div className="setup-door-form__section setup-door-form__section--trailer">
                        <label className="setup-door-form__label" htmlFor="setup-door-trailer">
                            Trailer number
                        </label>
                        <input
                            id="setup-door-trailer"
                            type="text"
                            className="setup-door-form__input"
                            value={trailerNumber}
                            onChange={(e) => setTrailerNumber(e.target.value)}
                            placeholder="Enter trailer number"
                        />
                    </div>
                    <div className="setup-door-form__section setup-door-form__section--carrier">
                        <label className="setup-door-form__label">Carrier</label>
                        <AsyncSelect<CarrierOption>
                            placeholder="Search carrier by name or number..."
                            loadOptions={loadOptions}
                            value={selectedCarrier}
                            onChange={(val) => {
                                const option = Array.isArray(val) ? null : val ?? null;
                                setSelectedCarrier(option as CarrierOption | null);
                            }}
                            isClearable
                            defaultOptions={[]}
                            noOptionsMessage={({ inputValue }) =>
                                !inputValue || inputValue.length < 2
                                    ? "Type at least 2 characters to search"
                                    : "No carriers found"
                            }
                            styles={customXGSSelectStyles}
                            classNamePrefix="xgs-select"
                            components={{ IndicatorSeparator: () => null }}
                        />
                    </div>
                </div>

                <div className="setup-door-form__actions">
                    <Button
                        theme={ButtonThemes.blue}
                        disabled={isSubmitDisabled}
                        spinner={setupDoorMutation.isLoading}
                        onClick={handleSubmit}
                    >
                        Assign door
                    </Button>
                </div>
            </div>
        </SlideOutSidebar>
    );
};
